import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { FlowpiDrawerSurface } from "../ui/flowpi-dialog";
import { Button } from "../ui/button";
import { formatApiError } from "../../lib/api";
import { getCannibalDeviceHistory } from "../../lib/serviceApi";

const EMPTY = "-";
const EVENT_TITLES = {
  donor_created: "Donor Created",
  donor_edited: "Donor Edited",
  pre_existing_removed_parts_changed: "Pre-existing Removed Parts Updated",
  part_used: "Part Used",
  part_reversed: "Part Reversed",
  transferred_to_service_stock: "Transferred to Service Stock",
  "cannibal_device.create": "Donor Created",
  "cannibal_device.edit": "Donor Edited",
  "cannibal_device.transfer": "Transferred to Service Stock",
  "service_ticket.cannibal_use": "Part Used",
  "service_ticket.cannibal_reversed": "Part Reversed",
};

const clean = (value) => (typeof value === "string" ? value.trim() : "");
const display = (value) => clean(value) || EMPTY;

function legacyValues(row) {
  const details = row?.details && typeof row.details === "object" ? row.details : {};
  const action = row?.event_type || row?.action;
  const values = [];
  const part = [clean(details.part_number), clean(details.part_name)].filter(Boolean).join(" — ");
  if (part) values.push({ label: "Part", value: part });
  if (["part_used", "part_reversed", "service_ticket.cannibal_use", "service_ticket.cannibal_reversed"].includes(action)) {
    values.push({ label: "Used for Ticket", value: clean(details.service_ticket_number) || null });
    values.push({ label: "Recipient SN", value: clean(details.recipient_serial_number) || null });
  }
  if (["part_reversed", "service_ticket.cannibal_reversed"].includes(action) && details.reversal_reason) {
    values.push({ label: "Reason", value: clean(details.reversal_reason) || null });
  }
  if (["transferred_to_service_stock", "cannibal_device.transfer"].includes(action)) {
    values.push({ label: "Created Stock Ticket", value: clean(details.service_ticket_number) || null });
  }
  return values;
}

export function normalizeCannibalHistory(response) {
  const data = response?.data || response || [];
  const records = Array.isArray(data) ? data : [];
  return records
    .map((row, index) => {
      const eventType = clean(row?.event_type || row?.action);
      const suppliedValues = Array.isArray(row?.values)
        ? row.values
            .filter((item) => item && typeof item === "object" && clean(item.label))
            .map((item) => ({ label: clean(item.label), value: item.value }))
        : [];
      return {
        key: `${clean(row?.timestamp || row?.created_at)}-${eventType}-${index}`,
        eventType,
        title: clean(row?.title) || EVENT_TITLES[eventType] || "History Event",
        timestamp: row?.timestamp || row?.created_at || null,
        actor: clean(row?.actor || row?.actor_email || row?.created_by_email) || null,
        values: suppliedValues.length > 0 ? suppliedValues : legacyValues(row),
      };
    })
    .sort((left, right) => {
      const timestampOrder = String(right.timestamp || "").localeCompare(String(left.timestamp || ""));
      return timestampOrder || right.key.localeCompare(left.key);
    });
}

function formatTimestamp(value) {
  if (!value) return EMPTY;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? EMPTY : date.toLocaleString();
}

export default function CannibalHistoryDrawer({ donor, onClose }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    getCannibalDeviceHistory(donor.id)
      .then((response) => {
        if (!cancelled) setRecords(normalizeCannibalHistory(response));
      })
      .catch((reason) => {
        if (!cancelled) {
          setRecords([]);
          setError(formatApiError(reason));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [donor.id]);

  const content = useMemo(() => {
    if (loading) return <p className="text-sm text-zinc-500">Loading history...</p>;
    if (error) return <p className="text-sm text-destructive">{error}</p>;
    if (records.length === 0) return <p className="text-sm text-zinc-500">No history events.</p>;
    return records.map((event) => (
      <article key={event.key} className="rounded-sm border border-zinc-200 p-4 dark:border-zinc-700" data-event-type={event.eventType || "unknown"}>
        <h3 className="break-words text-sm font-semibold text-zinc-950 dark:text-zinc-100">{event.title}</h3>
        <p className="mt-1 break-words text-xs text-zinc-500">
          {formatTimestamp(event.timestamp)} <span aria-hidden="true">·</span> {display(event.actor)}
        </p>
        {event.values.length > 0 ? (
          <dl className="mt-3 space-y-2">
            {event.values.map((item, index) => (
              <div key={`${item.label}-${index}`} className="grid grid-cols-[minmax(120px,0.42fr)_minmax(0,1fr)] gap-3 text-xs">
                <dt className="break-words font-medium text-zinc-500">{item.label}</dt>
                <dd className="min-w-0 break-words text-zinc-800 dark:text-zinc-200">{display(item.value)}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </article>
    ));
  }, [error, loading, records]);

  return (
    <FlowpiDrawerSurface open onClose={onClose} accessibleTitle="Cannibal History" testId="cannibal-history-drawer">
      <aside className="ml-auto flex h-full w-full max-w-[560px] flex-col border-l border-zinc-200 bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-950" data-side="right">
        <header className="flex items-start justify-between gap-4 border-b border-zinc-200 px-6 py-5 dark:border-zinc-700">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">Cannibal History</div>
            <h2 id="cannibal-history-title" className="mt-1 break-words text-xl font-semibold">{display(donor.serial_number)}</h2>
          </div>
          <Button variant="ghost" onClick={onClose} data-action-category="close">
            <X className="h-4 w-4" />
            Close
          </Button>
        </header>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-5">{content}</div>
      </aside>
    </FlowpiDrawerSurface>
  );
}
